import { createPayloadAction, createRequestActionCreators, RequestAction } from 'helpers/actionsHelpers'
import { PayloadAction } from 'interfaces/action'
import {
  CtaInsightOptions,
  Insight,
  InsightCTAFullContainer,
  InsightCTAKey,
  InsightId,
  InsightsCollection,
  PlacementType,
  InsightWithNotificatonsPlacement,
} from 'interfaces/insights'
import { Timeframe } from 'interfaces/chart'
import { MoneyflowType } from 'interfaces/moneyflow'
import { AccountType } from 'interfaces/accounts'
import { CategoryId } from 'interfaces/meta'
import { isAccountPage, isMoneyflowPage } from 'helpers/pageHelpers'
import { InsightsPageType } from 'interfaces/pages'
import { RelationId } from 'interfaces/peergroups'
import { SocialInnerInsight } from 'interfaces/socialInsights'
import { Collection } from 'interfaces'

export const GET_INSIGHTS = 'GET_INSIGHTS' as const
export type GetInsightsPayloadBase = {
  timeframe: Timeframe
  peersRelationId: number
  othersRelationId: number
}
type OtherInsightsPageType = Exclude<InsightsPageType, 'transactions' | MoneyflowType | AccountType>
type GetInsightsPayloadUncategorized = GetInsightsPayloadBase & {
  page: OtherInsightsPageType
}
type GetInsightsPayloadMoneyflow = GetInsightsPayloadBase & {
  page: MoneyflowType
  category: CategoryId
}
type GetInsightsPayloadAccount = GetInsightsPayloadBase & {
  page: AccountType
  category: CategoryId
}
type GetInsightsUncategorized = PayloadAction<typeof GET_INSIGHTS, GetInsightsPayloadUncategorized>
type GetInsightsMoneyflow = PayloadAction<typeof GET_INSIGHTS, GetInsightsPayloadMoneyflow>
type GetInsightsAccount = PayloadAction<typeof GET_INSIGHTS, GetInsightsPayloadAccount>
type GetInsights = GetInsightsUncategorized | GetInsightsMoneyflow | GetInsightsAccount
export function getInsightsActionCreator (page: OtherInsightsPageType, timeframe: Timeframe, peersRelationId: number, othersRelationId: number): GetInsightsUncategorized
export function getInsightsActionCreator (page: AccountType, timeframe: Timeframe, peersRelationId: number, othersRelationId: number, category: CategoryId): GetInsightsAccount
export function getInsightsActionCreator (page: MoneyflowType, timeframe: Timeframe, peersRelationId: number, othersRelationId: number, category: CategoryId): GetInsightsMoneyflow
export function getInsightsActionCreator (page: OtherInsightsPageType | MoneyflowType | AccountType, timeframe: Timeframe, peersRelationId: number, othersRelationId: number, category?: CategoryId): GetInsights {
  if (isAccountPage(page)) {
    const typedCategory = category as CategoryId
    return createPayloadAction(GET_INSIGHTS, { page, timeframe, peersRelationId, othersRelationId, category: typedCategory })
  }

  if (isMoneyflowPage(page)) {
    const typedCategory = category as CategoryId
    return createPayloadAction(GET_INSIGHTS, { page, timeframe, peersRelationId, othersRelationId, category: typedCategory })
  }

  return createPayloadAction(GET_INSIGHTS, { page, timeframe, peersRelationId, othersRelationId })
}

export const SET_INSIGHTS = 'SET_INSIGHTS' as const
type SetInsightsPayload = {
  placements: InsightWithNotificatonsPlacement[]
  insights: InsightsCollection
  isCurrentPageInsights: boolean
}
export type SetInsights = PayloadAction<typeof SET_INSIGHTS, SetInsightsPayload>
export const setInsightsActionCreator = (insights: InsightsCollection, placements: InsightWithNotificatonsPlacement[], isCurrentPageInsights: boolean): SetInsights =>
  createPayloadAction(SET_INSIGHTS, { placements, insights, isCurrentPageInsights })

type ResetInsightsPayload = {
  priorRelationId: RelationId | null
}
const DEFAULT_RESET_INSIGHTS_PAYLOAD: ResetInsightsPayload = {
  priorRelationId: null,
}
export const RESET_INSIGHTS = 'RESET_INSIGHTS' as const
type ResetInsights = PayloadAction<typeof RESET_INSIGHTS, ResetInsightsPayload>
export const resetInsightsActionCreator = (params: ResetInsightsPayload = DEFAULT_RESET_INSIGHTS_PAYLOAD): ResetInsights =>
  createPayloadAction(RESET_INSIGHTS, { ...DEFAULT_RESET_INSIGHTS_PAYLOAD, ...params })

export const INSIGHT_SHOWN = 'INSIGHT_SHOWN' as const
type InsightShown = PayloadAction<typeof INSIGHT_SHOWN, Insight | SocialInnerInsight>
export const insightShownActionCreator = (insight: Insight | SocialInnerInsight): InsightShown =>
  createPayloadAction(INSIGHT_SHOWN, insight)

type ToggleInsightDismissPayload = { insight: Insight | SocialInnerInsight, is_dismissed: boolean }

export const TOGGLE_DISMISS_INSIGHT = 'TOGGLE_DISMISS_INSIGHT' as const
type ToggleDismissInsight = PayloadAction<typeof TOGGLE_DISMISS_INSIGHT, ToggleInsightDismissPayload>
export const toggleDismissInsightActionCreator = (insight: Insight | SocialInnerInsight, is_dismissed: boolean): ToggleDismissInsight =>
  createPayloadAction(TOGGLE_DISMISS_INSIGHT, { insight, is_dismissed })

export const FAIL_TOGGLE_DISMISS_INSIGHT = 'FAIL_TOGGLE_DISMISS_INSIGHT' as const
type FailToggleDismissInsight = PayloadAction<typeof FAIL_TOGGLE_DISMISS_INSIGHT, ToggleInsightDismissPayload>
export const failToggleDismissInsightActionCreator = (insight: Insight | SocialInnerInsight, is_dismissed: boolean): FailToggleDismissInsight =>
  createPayloadAction(FAIL_TOGGLE_DISMISS_INSIGHT, { insight, is_dismissed })

export const UNDISMISS_INSIGHTS_BY_PLACEMENT = 'UNDISMISS_INSIGHTS_BY_PLACEMENT' as const
type UndismissInsightByPlacementPayload =
  | { placement: PlacementType, insights: (Insight | SocialInnerInsight)[], silent?: boolean }
type UndismissInsightsByPlacement = RequestAction<typeof UNDISMISS_INSIGHTS_BY_PLACEMENT, UndismissInsightByPlacementPayload>
export const undismissInsightsByPlacementActionCreator = createRequestActionCreators<
  typeof UNDISMISS_INSIGHTS_BY_PLACEMENT, UndismissInsightByPlacementPayload
>(UNDISMISS_INSIGHTS_BY_PLACEMENT)

// we use it for undismiss for example
export const UPDATE_PLACEMENT_INSIGHTS = 'UPDATE_PLACEMENT_INSIGHTS' as const
type UpdatePlacementInsights = PayloadAction<typeof UPDATE_PLACEMENT_INSIGHTS, { placement: PlacementType, insights: Collection<Insight | SocialInnerInsight, 'id'> }>
export const updatePlacementInsightsActionCreator = (placement: PlacementType, insights: Collection<Insight | SocialInnerInsight, 'id'>): UpdatePlacementInsights =>
  createPayloadAction(UPDATE_PLACEMENT_INSIGHTS, { placement, insights })

export const SEE_NEXT_INSIGHT = 'SEE_NEXT_INSIGHT' as const
type SeeNextInsight = PayloadAction<typeof SEE_NEXT_INSIGHT, Insight | SocialInnerInsight>
export const seeNextInsightActionCreator = (payload: Insight | SocialInnerInsight): SeeNextInsight =>
  createPayloadAction(SEE_NEXT_INSIGHT, payload)

export const SET_ACTIVE_INSIGHT = 'SET_ACTIVE_INSIGHT' as const
type SetActiveInsightPayload = {
  placement: PlacementType
  id: InsightId
}
type SetActiveInsight = PayloadAction<typeof SET_ACTIVE_INSIGHT, SetActiveInsightPayload>
export const setActiveInsightActionCreator = (payload: SetActiveInsightPayload): SetActiveInsight =>
  createPayloadAction(SET_ACTIVE_INSIGHT, payload)

type InsightCtaClickPayload<T extends InsightCTAKey> = {
  type: T
  insight?: Insight | SocialInnerInsight
  cta: InsightCTAFullContainer[T]
  options: CtaInsightOptions
}
const INSIGHT_CTA_CLICK_DEFAULT_OPTIONS: CtaInsightOptions = {
  isCausingInsightsReset: false,
}
export const INSIGHT_CTA_CLICK = 'INSIGHT_CTA_CLICK' as const
type InsightCtaClick<T extends InsightCTAKey> = PayloadAction<typeof INSIGHT_CTA_CLICK, InsightCtaClickPayload<T>>
export const insightCtaClickActionCreator = <T extends InsightCTAKey>(type: T, cta: InsightCTAFullContainer[T], insight?: Insight | SocialInnerInsight, options = INSIGHT_CTA_CLICK_DEFAULT_OPTIONS): InsightCtaClick<T> =>
  createPayloadAction(INSIGHT_CTA_CLICK, { type, cta, insight, options: { ...INSIGHT_CTA_CLICK_DEFAULT_OPTIONS, ...options }})
type InsightCtaClickAction =
  | InsightCtaClick<'link'>
  | InsightCtaClick<'set_tracker'>
  | InsightCtaClick<'edit_tracker'>
  | InsightCtaClick<'dismiss'>
  | InsightCtaClick<'edit'>
  | InsightCtaClick<'add_account'>
  | InsightCtaClick<'add_car'>
  | InsightCtaClick<'add_home'>
  | InsightCtaClick<'update_credentials'>
  | InsightCtaClick<'view_post'>
  | InsightCtaClick<'view_comment'>
  | InsightCtaClick<'allow_push_notifications'>
  | InsightCtaClick<'popup'>
  | InsightCtaClick<'meeting_info'>

export const FETCH_CTA_FROM_URL = 'FETCH_CTA_FROM_URL' as const
export const fetchCTAFromURLActionCreator =
  (): PayloadAction<typeof FETCH_CTA_FROM_URL, {}> =>
    createPayloadAction(FETCH_CTA_FROM_URL, {})
type FetchCTAFromURL = ReturnType<typeof fetchCTAFromURLActionCreator>

export type InsightsActions =
  | GetInsights
  | SetInsights
  | ResetInsights
  | ToggleDismissInsight
  | FailToggleDismissInsight
  | UndismissInsightsByPlacement
  | UpdatePlacementInsights
  | SeeNextInsight
  | SetActiveInsight
  | InsightShown
  | InsightCtaClickAction
  | FetchCTAFromURL

export default InsightsActions
